// src/Components/Documents/FamilyVaultTab.js
import React, { useState, useEffect, useCallback } from 'react';
import logger from '../../utils/logger';
import {
  Box, Typography, Button, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, LinearProgress, Alert, Menu, MenuItem, Snackbar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Breadcrumbs, Link, useMediaQuery, useTheme, ToggleButton, ToggleButtonGroup,
  Card, CardContent, CardActions, Grid, InputAdornment, FormControl, Select,
  InputLabel, Checkbox, Fade, Chip, List, ListItem, ListItemText,
  ListItemSecondaryAction, FormControlLabel, Radio, RadioGroup, FormLabel
} from '@mui/material';
import {
  Upload as UploadIcon, Download as DownloadIcon, Delete as DeleteIcon,
  Share as ShareIcon, Folder as FolderIcon, InsertDriveFile as FileIcon,
  MoreVert as MoreVertIcon, CreateNewFolder as CreateFolderIcon,
  NavigateNext as NavigateNextIcon, CloudUpload as CloudUploadIcon,
  Edit as EditIcon, ViewList as ViewListIcon, ViewModule as ViewModuleIcon,
  ViewComfy as ViewComfyIcon, Image as ImageIcon, Search as SearchIcon,
  ArrowUpward as ArrowUpwardIcon, ArrowDownward as ArrowDownwardIcon,
  DriveFileMove as MoveIcon, DeleteSweep as TrashIcon, Restore as RestoreIcon,
  DeleteForever as DeleteForeverIcon, ContentCopy as CopyIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useProfile } from '../../contexts/ProfileContext';
import ImageLightbox from './ImageLightbox';
import {
  getFamilyVaultDocuments, formatFileSize, getFileIcon,
  grantVaultAccess, revokeVaultAccess,
  createFamilyVaultFolder, uploadToFamilyVaultWithPath,
  renameFamilyVaultDocument, moveFamilyVaultDocument, copyFamilyVaultDocumentToPersonal
} from '../../services/familyDocumentService';
import { db, storage } from '../../firebase';
import { doc, updateDoc, serverTimestamp, collection, getDocs, deleteDoc } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { downloadFile } from '../../utils/downloadUtils';

const extractStoragePathFromUrl = (url) => {
  try {
    const m = url && url.match(/\/o\/([^?]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
};

/**
 * Helper: Get user-friendly file type label from a document
 */
const getFileTypeLabel = (docItem) => {
  const fileName = docItem.fileName || docItem.title || docItem.name || '';
  const extension = fileName.split('.').pop()?.toLowerCase();

  if (extension && extension !== fileName.toLowerCase()) {
    return extension.toUpperCase();
  }

  const mimeType = docItem.fileType || docItem.mimeType || '';
  const mimeMap = {
    'application/pdf': 'PDF',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint',
    'application/msword': 'Word',
    'application/vnd.ms-excel': 'Excel',
    'application/vnd.ms-powerpoint': 'PowerPoint',
    'text/plain': 'Text',
    'image/jpeg': 'JPEG',
    'image/png': 'PNG',
    'image/gif': 'GIF',
    'image/svg+xml': 'SVG'
  };

  if (mimeMap[mimeType]) {
    return mimeMap[mimeType];
  }

  const shortType = mimeType.split('/')[1];
  return shortType && shortType.length < 10 ? shortType.toUpperCase() : 'File';
};

/**
 * Vault categories for document classification
 */
const VAULT_CATEGORIES = [
  { value: 'legal', label: 'Legal Documents' },
  { value: 'financial', label: 'Financial' },
  { value: 'health', label: 'Health Records' },
  { value: 'estate', label: 'Estate Planning' },
  { value: 'tax', label: 'Tax Documents' },
  { value: 'property', label: 'Property Documents' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'other', label: 'Other' }
];

/**
 * FamilyVaultTab Component
 *
 * Full-featured family vault with folder navigation, search, sort, multiple view modes,
 * drag-and-drop upload, soft-delete trash, batch operations, context menus, share/access
 * control, rename, move, copy to personal, and image lightbox.
 *
 * Props:
 * @param {string} fileTypeFilter - Optional MIME type filter (e.g., 'image/*')
 * @param {string} defaultView - Default view mode: 'list', 'grid', or 'gallery'
 * @param {string} title - Component title
 * @param {string} uploadButtonText - Upload button label
 * @param {string} emptyMessage - Message when vault is empty
 */
const FamilyVaultTab = ({
  fileTypeFilter = null,
  defaultView = 'list',
  title = 'Family Vault',
  uploadButtonText = 'Upload to Vault',
  emptyMessage = 'No vault documents yet. Upload sensitive files to share securely with specific family members.',
  storageType: storageTypeProp = null,
  acceptFileTypes = null,
  dialogTitle = null,
  categoryOptions = null
} = {}) => {
  const { currentUser } = useAuth();
  const { profile } = useProfile();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Determine storageType from prop or fileTypeFilter
  const storageType = storageTypeProp || (fileTypeFilter === 'image/*' ? 'photos' : 'documents');
  const categories = categoryOptions || VAULT_CATEGORIES;
  const uploadDialogTitle = dialogTitle || (storageType === 'photos' ? 'Upload to Family Photo Vault' : 'Upload to Family Vault');

  // ---- Core state ----
  const [allDocuments, setAllDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ---- Folder navigation ----
  const [currentPath, setCurrentPath] = useState('/');

  // ---- Search / Sort ----
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  // ---- View mode ----
  const [viewMode, setViewMode] = useState(() => {
    const storageKey = fileTypeFilter === 'image/*' ? 'familyVaultPhotosViewMode' : 'familyVaultViewMode';
    return localStorage.getItem(storageKey) || defaultView;
  });

  // ---- Trash ----
  const [viewingTrash, setViewingTrash] = useState(false);

  // ---- Drag & drop ----
  const [isDragging, setIsDragging] = useState(false);

  // ---- Upload dialog ----
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadCategory, setUploadCategory] = useState(() => categories[0]?.value || 'legal');

  // ---- Delete dialog (soft delete / trash) ----
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ---- Permanent delete dialog ----
  const [permanentDeleteDialogOpen, setPermanentDeleteDialogOpen] = useState(false);
  const [documentToPermanentDelete, setDocumentToPermanentDelete] = useState(null);
  const [permanentDeleting, setPermanentDeleting] = useState(false);

  // ---- Folder creation ----
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  // ---- Rename ----
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [documentToRename, setDocumentToRename] = useState(null);

  // ---- Move ----
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [documentToMove, setDocumentToMove] = useState(null);
  const [moveDestination, setMoveDestination] = useState('/');

  // ---- Share / Access Control ----
  const [openShareDialog, setOpenShareDialog] = useState(false);
  const [documentToShare, setDocumentToShare] = useState(null);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [sharePermission, setSharePermission] = useState('view');
  const [sharing, setSharing] = useState(false);

  // ---- Batch selection ----
  const [selectedIds, setSelectedIds] = useState(new Set());

  // ---- Context menu ----
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuItem, setMenuItem] = useState(null);

  // ---- Snackbar ----
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ---- Lightbox ----
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // ---- Copy to personal ----
  const [copying, setCopying] = useState(false);

  // ===========================================
  // DATA LOADING
  // ===========================================

  /**
   * Load all vault documents by storageType, then filter client-side
   */
  const loadDocuments = useCallback(async () => {
    if (!profile?.familyCode) return;

    try {
      setLoading(true);
      setError(null);
      const docs = await getFamilyVaultDocuments(profile.familyCode, storageType);
      setAllDocuments(docs);
    } catch (err) {
      logger.error('Error loading family vault documents:', err);
      setError(err.message || 'Failed to load vault documents');
    } finally {
      setLoading(false);
    }
  }, [profile, storageType]);

  /**
   * Load family members for the share dialog
   */
  const loadFamilyMembers = useCallback(async () => {
    if (!profile?.familyCode || !currentUser) return;

    try {
      const membersRef = collection(db, 'families', profile.familyCode, 'members');
      const membersSnap = await getDocs(membersRef);
      const members = [];
      membersSnap.forEach((docSnap) => {
        const memberData = docSnap.data();
        if (docSnap.id !== currentUser.uid) {
          members.push({
            userId: docSnap.id,
            userName: memberData.displayName || ((memberData.firstName || '') + ' ' + (memberData.lastName || '')).trim() || memberData.email || 'Unknown',
            email: memberData.email || ''
          });
        }
      });
      setFamilyMembers(members);
    } catch (err) {
      logger.error('Error loading family members:', err);
    }
  }, [profile, currentUser]);

  useEffect(() => {
    loadDocuments();
    loadFamilyMembers();
  }, [loadDocuments, loadFamilyMembers]);

  // ===========================================
  // FILTERING, SEARCH, SORT
  // ===========================================

  /**
   * Check if a document is an image
   */
  const isImage = (docItem) => {
    const mimeType = docItem.fileType || docItem.mimeType || '';
    return mimeType.startsWith('image/');
  };

  /**
   * Get documents filtered by current path / trash / search / sort
   */
  const getProcessedDocuments = useCallback(() => {
    let docs = [...allDocuments];

    // Filter by trash status
    if (viewingTrash) {
      docs = docs.filter(d => d.trashed === true);
    } else {
      docs = docs.filter(d => d.trashed !== true);
    }

    // Filter by folder path (only when not in trash view)
    if (!viewingTrash) {
      docs = docs.filter(d => {
        const docFolder = d.folderPath || '/';
        const current = currentPath || '/';
        return docFolder === current;
      });
    }

    // Gallery view: only images and folders
    if (viewMode === 'gallery') {
      docs = docs.filter(d => d.type === 'folder' || isImage(d));
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      docs = docs.filter(d => {
        const name = (d.fileName || d.title || d.name || '').toLowerCase();
        return name.includes(query);
      });
    }

    // Sort
    docs.sort((a, b) => {
      // Folders always first
      if (a.type === 'folder' && b.type !== 'folder') return -1;
      if (a.type !== 'folder' && b.type === 'folder') return 1;

      let comparison = 0;
      switch (sortBy) {
        case 'name': {
          const nameA = (a.fileName || a.title || a.name || '').toLowerCase();
          const nameB = (b.fileName || b.title || b.name || '').toLowerCase();
          comparison = nameA.localeCompare(nameB);
          break;
        }
        case 'date': {
          const dateA = a.uploadedAt?.toMillis?.() || a.uploadedAt?.seconds * 1000 || 0;
          const dateB = b.uploadedAt?.toMillis?.() || b.uploadedAt?.seconds * 1000 || 0;
          comparison = dateA - dateB;
          break;
        }
        case 'size': {
          const sizeA = a.fileSize || 0;
          const sizeB = b.fileSize || 0;
          comparison = sizeA - sizeB;
          break;
        }
        case 'type': {
          const typeA = a.type === 'folder' ? 'Folder' : getFileTypeLabel(a);
          const typeB = b.type === 'folder' ? 'Folder' : getFileTypeLabel(b);
          comparison = typeA.localeCompare(typeB);
          break;
        }
        default:
          comparison = 0;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return docs;
  }, [allDocuments, viewingTrash, currentPath, viewMode, searchQuery, sortBy, sortOrder]);

  /**
   * Get all folders from all documents (for move dialog)
   */
  const getAllFolders = useCallback(() => {
    return allDocuments
      .filter(d => d.type === 'folder' && d.trashed !== true)
      .map(f => {
        const parentPath = f.folderPath || '/';
        const folderName = f.fileName || f.name || 'Folder';
        const fullPath = parentPath === '/' ? `/${folderName}` : `${parentPath}/${folderName}`;
        return { id: f.id, name: folderName, path: fullPath, parentPath };
      });
  }, [allDocuments]);

  // ===========================================
  // BREADCRUMB NAVIGATION
  // ===========================================

  const getBreadcrumbs = () => {
    return currentPath.split('/').filter(Boolean);
  };

  const handleBreadcrumbClick = (index) => {
    const segments = getBreadcrumbs();
    const newPath = '/' + segments.slice(0, index + 1).join('/');
    setCurrentPath(newPath);
  };

  // ===========================================
  // FOLDER NAVIGATION
  // ===========================================

  const handleFolderClick = (folder) => {
    const folderName = folder.fileName || folder.name;
    const newPath = currentPath === '/' ? `/${folderName}` : `${currentPath}/${folderName}`;
    setCurrentPath(newPath);
    setSelectedIds(new Set());
  };

  // ===========================================
  // DRAG & DROP
  // ===========================================

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.target === e.currentTarget) {
      setIsDragging(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setFilesToUpload(files);
      setUploadDialogOpen(true);
    }
  };

  // ===========================================
  // VIEW MODE
  // ===========================================

  const handleViewModeChange = (event, newView) => {
    if (newView !== null) {
      setViewMode(newView);
      const storageKey = fileTypeFilter === 'image/*' ? 'familyVaultPhotosViewMode' : 'familyVaultViewMode';
      localStorage.setItem(storageKey, newView);
    }
  };

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  // ===========================================
  // FILE UPLOAD
  // ===========================================

  const handleUpload = async () => {
    if (filesToUpload.length === 0) return;

    setUploading(true);
    setUploadError(null);

    try {
      const uploaderName = profile?.displayName
        || ((profile?.firstName || '') + ' ' + (profile?.lastName || '')).trim()
        || currentUser.email;

      for (const file of filesToUpload) {
        const metadata = {
          category: uploadCategory,
          storageType,
          uploadedBy: currentUser.uid,
          uploadedByName: uploaderName
        };
        await uploadToFamilyVaultWithPath(profile.familyCode, file, metadata, currentPath);
      }

      setSnackbar({
        open: true,
        message: `Successfully uploaded ${filesToUpload.length} file(s) to vault`,
        severity: 'success'
      });

      await loadDocuments();
      setUploadDialogOpen(false);
      setFilesToUpload([]);
      setUploadCategory(categories[0]?.value || 'legal');
    } catch (err) {
      logger.error('Error uploading to family vault:', err);
      setUploadError(err.message || 'Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  // ===========================================
  // FOLDER CREATION
  // ===========================================

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      const uploaderName = profile?.displayName
        || ((profile?.firstName || '') + ' ' + (profile?.lastName || '')).trim()
        || currentUser.email;

      await createFamilyVaultFolder(
        profile.familyCode,
        newFolderName.trim(),
        currentPath,
        currentUser.uid,
        uploaderName,
        storageType
      );

      setSnackbar({
        open: true,
        message: `Created folder "${newFolderName.trim()}"`,
        severity: 'success'
      });

      await loadDocuments();
      setFolderDialogOpen(false);
      setNewFolderName('');
    } catch (err) {
      logger.error('Error creating vault folder:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to create folder',
        severity: 'error'
      });
    }
  };

  // ===========================================
  // SOFT DELETE (TRASH)
  // ===========================================

  const handleTrashClick = (docItem) => {
    setDocumentToDelete(docItem);
    setDeleteDialogOpen(true);
  };

  const handleSoftDelete = async () => {
    if (!documentToDelete) return;

    setDeleting(true);
    try {
      const docRef = doc(db, 'families', profile.familyCode, 'familyVault', documentToDelete.id);
      await updateDoc(docRef, {
        trashed: true,
        trashedAt: serverTimestamp(),
        trashedBy: currentUser.uid
      });

      setSnackbar({
        open: true,
        message: `"${documentToDelete.fileName}" moved to trash`,
        severity: 'success'
      });

      await loadDocuments();
      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
    } catch (err) {
      logger.error('Error trashing vault document:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to move to trash',
        severity: 'error'
      });
    } finally {
      setDeleting(false);
    }
  };

  /**
   * Batch soft delete selected items
   */
  const handleBatchTrash = async () => {
    if (selectedIds.size === 0) return;

    try {
      for (const itemId of selectedIds) {
        const docRef = doc(db, 'families', profile.familyCode, 'familyVault', itemId);
        await updateDoc(docRef, {
          trashed: true,
          trashedAt: serverTimestamp(),
          trashedBy: currentUser.uid
        });
      }

      setSnackbar({
        open: true,
        message: `Moved ${selectedIds.size} item(s) to trash`,
        severity: 'success'
      });

      setSelectedIds(new Set());
      await loadDocuments();
    } catch (err) {
      logger.error('Error batch trashing:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to move items to trash',
        severity: 'error'
      });
    }
  };

  // ===========================================
  // RESTORE FROM TRASH
  // ===========================================

  const handleRestore = async (docItem) => {
    try {
      const docRef = doc(db, 'families', profile.familyCode, 'familyVault', docItem.id);
      await updateDoc(docRef, {
        trashed: false,
        trashedAt: null,
        trashedBy: null
      });

      setSnackbar({
        open: true,
        message: `"${docItem.fileName}" restored from trash`,
        severity: 'success'
      });

      await loadDocuments();
    } catch (err) {
      logger.error('Error restoring vault document:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to restore document',
        severity: 'error'
      });
    }
  };

  // ===========================================
  // PERMANENT DELETE
  // ===========================================

  const handlePermanentDeleteClick = (docItem) => {
    setDocumentToPermanentDelete(docItem);
    setPermanentDeleteDialogOpen(true);
  };

  const handlePermanentDelete = async () => {
    if (!documentToPermanentDelete) return;

    setPermanentDeleting(true);
    try {
      // If it has a storage path, delete from storage
      if (documentToPermanentDelete.filePath) {
        try {
          const storageRef = ref(storage, documentToPermanentDelete.filePath);
          await deleteObject(storageRef);
        } catch (storageErr) {
          logger.error('Error deleting file from storage (may already be deleted):', storageErr);
        }
      }

      // Delete Firestore document
      const docRef = doc(db, 'families', profile.familyCode, 'familyVault', documentToPermanentDelete.id);
      await deleteDoc(docRef);

      setSnackbar({
        open: true,
        message: `"${documentToPermanentDelete.fileName}" permanently deleted`,
        severity: 'success'
      });

      await loadDocuments();
      setPermanentDeleteDialogOpen(false);
      setDocumentToPermanentDelete(null);
    } catch (err) {
      logger.error('Error permanently deleting vault document:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to permanently delete',
        severity: 'error'
      });
    } finally {
      setPermanentDeleting(false);
    }
  };

  // ===========================================
  // DOWNLOAD
  // ===========================================

  const handleDownload = async (docItem) => {
    const filename = docItem.fileName || docItem.title || docItem.name || 'download';
    const url = docItem.fileUrl || docItem.downloadURL;
    const storagePath = docItem.storagePath || extractStoragePathFromUrl(url);
    setSnackbar({ open: true, message: `Downloading ${filename}…`, severity: 'info' });
    try {
      await downloadFile(url, filename, storagePath);
      setSnackbar({ open: true, message: `"${filename}" downloaded`, severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to download file', severity: 'error' });
    }
  };

  // ===========================================
  // RENAME
  // ===========================================

  const handleRenameClick = (docItem) => {
    setDocumentToRename(docItem);
    setRenameValue(docItem.fileName || '');
    setRenameDialogOpen(true);
  };

  const handleRename = async () => {
    if (!renameValue.trim() || !documentToRename) return;

    try {
      await renameFamilyVaultDocument(profile.familyCode, documentToRename.id, renameValue.trim());

      setSnackbar({
        open: true,
        message: `Renamed to "${renameValue.trim()}"`,
        severity: 'success'
      });

      await loadDocuments();
      setRenameDialogOpen(false);
      setRenameValue('');
      setDocumentToRename(null);
    } catch (err) {
      logger.error('Error renaming vault document:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to rename',
        severity: 'error'
      });
    }
  };

  // ===========================================
  // MOVE
  // ===========================================

  const handleMoveClick = (docItem) => {
    setDocumentToMove(docItem);
    setMoveDestination('/');
    setMoveDialogOpen(true);
  };

  const handleMove = async () => {
    if (!documentToMove) return;

    try {
      await moveFamilyVaultDocument(profile.familyCode, documentToMove.id, moveDestination);

      const destDisplay = moveDestination === '/' ? 'Root' : moveDestination;
      setSnackbar({
        open: true,
        message: `Moved "${documentToMove.fileName}" to ${destDisplay}`,
        severity: 'success'
      });

      await loadDocuments();
      setMoveDialogOpen(false);
      setDocumentToMove(null);
    } catch (err) {
      logger.error('Error moving vault document:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to move document',
        severity: 'error'
      });
    }
  };

  // ===========================================
  // COPY TO PERSONAL
  // ===========================================

  const handleCopyToPersonal = async (docItem) => {
    if (!docItem || docItem.type === 'folder') {
      setSnackbar({
        open: true,
        message: 'Cannot copy folders to personal documents',
        severity: 'warning'
      });
      return;
    }

    setCopying(true);
    try {
      await copyFamilyVaultDocumentToPersonal(profile.familyCode, docItem.id, currentUser.uid);

      setSnackbar({
        open: true,
        message: `"${docItem.fileName}" copied to your personal documents`,
        severity: 'success'
      });
    } catch (err) {
      logger.error('Error copying to personal:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to copy to personal documents',
        severity: 'error'
      });
    } finally {
      setCopying(false);
    }
  };

  // ===========================================
  // SHARE / ACCESS CONTROL
  // ===========================================

  const handleShareClick = (docItem) => {
    setDocumentToShare(docItem);
    setSelectedMembers([]);
    setSharePermission('view');
    setOpenShareDialog(true);
  };

  const handleMemberToggle = (memberId) => {
    setSelectedMembers(prev => {
      if (prev.includes(memberId)) {
        return prev.filter(id => id !== memberId);
      }
      return [...prev, memberId];
    });
  };

  const handleGrantAccess = async () => {
    if (selectedMembers.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select at least one member',
        severity: 'warning'
      });
      return;
    }

    setSharing(true);
    try {
      const userAccess = selectedMembers.map((userId) => {
        const member = familyMembers.find(m => m.userId === userId);
        return {
          userId,
          userName: member?.userName || 'Unknown',
          permissions: sharePermission
        };
      });

      await grantVaultAccess(
        profile.familyCode,
        documentToShare.id,
        userAccess,
        currentUser.uid
      );

      setSnackbar({
        open: true,
        message: `Shared "${documentToShare.fileName}" with ${selectedMembers.length} member(s)`,
        severity: 'success'
      });

      await loadDocuments();
      setOpenShareDialog(false);
      setDocumentToShare(null);
      setSelectedMembers([]);
    } catch (err) {
      logger.error('Error granting vault access:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to grant access',
        severity: 'error'
      });
    } finally {
      setSharing(false);
    }
  };

  const handleRevokeAccess = async (docItem, userId) => {
    try {
      await revokeVaultAccess(profile.familyCode, docItem.id, userId);

      setSnackbar({
        open: true,
        message: 'Access revoked successfully',
        severity: 'success'
      });

      await loadDocuments();

      // If share dialog is open for this doc, update it
      if (documentToShare && documentToShare.id === docItem.id) {
        const refreshedDoc = allDocuments.find(d => d.id === docItem.id);
        if (refreshedDoc) {
          setDocumentToShare({ ...refreshedDoc });
        }
      }
    } catch (err) {
      logger.error('Error revoking vault access:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to revoke access',
        severity: 'error'
      });
    }
  };

  // ===========================================
  // BATCH SELECTION
  // ===========================================

  const handleToggleSelect = (itemId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const handleSelectAll = (docs) => {
    if (selectedIds.size === docs.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(docs.map(d => d.id)));
    }
  };

  // ===========================================
  // CONTEXT MENU
  // ===========================================

  const handleMenuOpen = (event, item) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuItem(item);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuItem(null);
  };

  // ===========================================
  // IMAGE LIGHTBOX
  // ===========================================

  const handleImageClick = (clickedDoc) => {
    const processedDocs = getProcessedDocuments();
    const images = processedDocs.filter(d => d.type !== 'folder' && isImage(d));
    const index = images.findIndex(d => d.id === clickedDoc.id);

    if (index !== -1) {
      const lightboxImgs = images.map(d => ({
        ...d,
        fileUrl: d.downloadURL || d.fileUrl
      }));
      setLightboxImages(lightboxImgs);
      setLightboxIndex(index);
      setLightboxOpen(true);
    }
  };

  // ===========================================
  // HELPERS
  // ===========================================

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  const getFileIconComponent = (docItem) => {
    if (docItem.type === 'folder') {
      return <FolderIcon color="primary" />;
    }
    if (isImage(docItem)) {
      return <ImageIcon color="info" />;
    }
    const iconType = getFileIcon(docItem.fileType || '');
    switch (iconType) {
      case 'pdf':
        return <FileIcon color="error" />;
      case 'document':
        return <FileIcon color="primary" />;
      case 'spreadsheet':
        return <FileIcon color="success" />;
      default:
        return <FileIcon color="action" />;
    }
  };

  const getSharedWithLabel = (docItem) => {
    if (docItem.type === 'folder') return null;
    if (docItem.accessGrantedTo?.length > 0) {
      return (
        <Chip
          label={`${docItem.accessGrantedTo.length} member(s)`}
          size="small"
          color="success"
          variant="outlined"
        />
      );
    }
    return <Chip label="Admin only" size="small" variant="outlined" />;
  };

  const getCategoryLabel = (docItem) => {
    if (!docItem.category) return null;
    const cat = categories.find(c => c.value === docItem.category);
    return cat ? cat.label : docItem.category;
  };

  // ===========================================
  // MOVE DIALOG: FOLDER TREE
  // ===========================================

  const getFoldersInPath = (path) => {
    return getAllFolders().filter(f => f.parentPath === path);
  };

  const getMoveBreadcrumbs = () => {
    if (moveDestination === '/') return [{ name: 'Root', path: '/' }];
    const segments = moveDestination.split('/').filter(Boolean);
    const breadcrumbs = [{ name: 'Root', path: '/' }];
    let built = '';
    segments.forEach(seg => {
      built += '/' + seg;
      breadcrumbs.push({ name: seg, path: built });
    });
    return breadcrumbs;
  };

  // ===========================================
  // RENDER
  // ===========================================

  const processedDocs = getProcessedDocuments();
  const fileAcceptType = acceptFileTypes || (fileTypeFilter === 'image/*' ? 'image/*' : undefined);

  return (
    <Box
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{ position: 'relative' }}
    >
      {/* ===== HEADER ===== */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'flex-start',
        flexDirection: isMobile ? 'column' : 'row',
        gap: 2,
        mb: 2
      }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6">{title}</Typography>
          <Typography variant="body2" color="text.secondary">
            Restricted {storageType === 'photos' ? 'photos' : 'documents'}. Upload sensitive files and grant access to specific family members.
          </Typography>

          {/* Breadcrumb Navigation (not shown in trash mode) */}
          {!viewingTrash && (
            <Breadcrumbs
              separator={<NavigateNextIcon fontSize="small" />}
              aria-label="breadcrumb"
              sx={{ mt: 1 }}
            >
              <Link
                component="button"
                variant="body2"
                onClick={() => { setCurrentPath('/'); setSelectedIds(new Set()); }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: currentPath === '/' ? 'primary.main' : 'text.primary',
                  fontWeight: currentPath === '/' ? 600 : 400,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                <FolderIcon fontSize="small" />
                Root
              </Link>
              {getBreadcrumbs().map((segment, index) => {
                const isLast = index === getBreadcrumbs().length - 1;
                return (
                  <Link
                    key={index}
                    component="button"
                    variant="body2"
                    onClick={() => { handleBreadcrumbClick(index); setSelectedIds(new Set()); }}
                    sx={{
                      color: isLast ? 'primary.main' : 'text.primary',
                      fontWeight: isLast ? 600 : 400,
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    {segment}
                  </Link>
                );
              })}
            </Breadcrumbs>
          )}

          {viewingTrash && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Viewing Trash. Restore items or permanently delete them.
            </Alert>
          )}
        </Box>

        {/* Toolbar Buttons */}
        <Box sx={{
          display: 'flex',
          gap: 1,
          flexDirection: isMobile ? 'column' : 'row',
          flexWrap: 'wrap'
        }}>
          {!viewingTrash && (
            <>
              <Button
                variant="outlined"
                size="small"
                startIcon={<CreateFolderIcon />}
                onClick={() => setFolderDialogOpen(true)}
              >
                New Folder
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<UploadIcon />}
                onClick={() => setUploadDialogOpen(true)}
              >
                {uploadButtonText}
              </Button>
            </>
          )}
          <Button
            variant={viewingTrash ? 'contained' : 'outlined'}
            size="small"
            color={viewingTrash ? 'warning' : 'inherit'}
            startIcon={<TrashIcon />}
            onClick={() => {
              setViewingTrash(!viewingTrash);
              setSelectedIds(new Set());
            }}
          >
            {viewingTrash ? 'Exit Trash' : 'View Trash'}
          </Button>
        </Box>
      </Box>

      {/* ===== SEARCH, SORT, VIEW CONTROLS ===== */}
      <Box sx={{
        display: 'flex',
        gap: 2,
        mb: 2,
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'space-between'
      }}>
        {/* Search */}
        <TextField
          size="small"
          placeholder="Search files and folders..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: isMobile ? '100%' : 250,
            maxWidth: isMobile ? '100%' : 400
          }}
        />

        {/* Sort and View Controls */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Sort Dropdown */}
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel id="fv-sort-label">Sort by</InputLabel>
            <Select
              labelId="fv-sort-label"
              value={sortBy}
              label="Sort by"
              onChange={(e) => setSortBy(e.target.value)}
            >
              <MenuItem value="date">Date</MenuItem>
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="size">Size</MenuItem>
              <MenuItem value="type">Type</MenuItem>
            </Select>
          </FormControl>

          {/* Sort Order Toggle */}
          <IconButton size="small" onClick={toggleSortOrder} title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}>
            {sortOrder === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
          </IconButton>

          {/* View Mode Toggle */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
            aria-label="view mode"
          >
            <ToggleButton value="list" aria-label="list view">
              <ViewListIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="grid" aria-label="grid view">
              <ViewModuleIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="gallery" aria-label="gallery view">
              <ViewComfyIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* ===== BATCH ACTIONS BAR ===== */}
      {selectedIds.size > 0 && !viewingTrash && (
        <Fade in>
          <Paper sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 2, bgcolor: 'action.hover' }}>
            <Typography variant="body2" fontWeight={500}>
              {selectedIds.size} item(s) selected
            </Typography>
            <Button
              size="small"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleBatchTrash}
            >
              Move to Trash
            </Button>
            <Button
              size="small"
              onClick={() => setSelectedIds(new Set())}
            >
              Clear Selection
            </Button>
          </Paper>
        </Fade>
      )}

      {/* ===== ERROR ===== */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ===== LOADING ===== */}
      {loading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
        </Box>
      )}

      {/* ===== EMPTY STATE ===== */}
      {!loading && processedDocs.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {viewingTrash
            ? 'Trash is empty.'
            : (searchQuery ? 'No results found for your search.' : emptyMessage)}
        </Alert>
      )}

      {/* ===== DOCUMENT DISPLAY ===== */}
      {!loading && processedDocs.length > 0 && (() => {

        // ---- LIST VIEW ----
        if (viewMode === 'list') {
          return (
            <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
              <Table size={isMobile ? 'medium' : 'small'}>
                <TableHead>
                  <TableRow>
                    {!viewingTrash && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={selectedIds.size > 0 && selectedIds.size < processedDocs.length}
                          checked={processedDocs.length > 0 && selectedIds.size === processedDocs.length}
                          onChange={() => handleSelectAll(processedDocs)}
                          size="small"
                        />
                      </TableCell>
                    )}
                    <TableCell>Name</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">Size</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Shared With</TableCell>
                    <TableCell>Uploaded</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {processedDocs.map((docItem) => (
                    <TableRow
                      key={docItem.id}
                      hover
                      selected={selectedIds.has(docItem.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      {!viewingTrash && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedIds.has(docItem.id)}
                            onChange={() => handleToggleSelect(docItem.id)}
                            size="small"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </TableCell>
                      )}
                      <TableCell
                        onClick={() => {
                          if (docItem.type === 'folder') {
                            handleFolderClick(docItem);
                          } else if (isImage(docItem)) {
                            handleImageClick(docItem);
                          } else {
                            handleDownload(docItem);
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getFileIconComponent(docItem)}
                          <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                            {docItem.fileName || 'Untitled'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {docItem.type !== 'folder' && docItem.category && (
                          <Chip label={getCategoryLabel(docItem)} size="small" color="warning" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {docItem.type === 'folder' ? '-' : formatFileSize(docItem.fileSize || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {docItem.type === 'folder' ? 'Folder' : getFileTypeLabel(docItem)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {getSharedWithLabel(docItem)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(docItem.uploadedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {viewingTrash ? (
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); handleRestore(docItem); }}
                              title="Restore"
                              color="primary"
                            >
                              <RestoreIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); handlePermanentDeleteClick(docItem); }}
                              title="Delete permanently"
                              color="error"
                            >
                              <DeleteForeverIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, docItem)}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          );
        }

        // ---- GRID VIEW ----
        if (viewMode === 'grid') {
          return (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {processedDocs.map((docItem) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={docItem.id}>
                  <Card
                    sx={{
                      height: '100%',
                      cursor: 'pointer',
                      position: 'relative',
                      border: selectedIds.has(docItem.id) ? '2px solid' : '1px solid',
                      borderColor: selectedIds.has(docItem.id) ? 'primary.main' : 'divider',
                      '&:hover': { boxShadow: 4 }
                    }}
                    onClick={() => {
                      if (viewingTrash) return;
                      if (docItem.type === 'folder') {
                        handleFolderClick(docItem);
                      } else if (isImage(docItem)) {
                        handleImageClick(docItem);
                      } else {
                        handleDownload(docItem);
                      }
                    }}
                  >
                    {/* Selection checkbox */}
                    {!viewingTrash && (
                      <Checkbox
                        checked={selectedIds.has(docItem.id)}
                        onChange={() => handleToggleSelect(docItem.id)}
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                        sx={{ position: 'absolute', top: 4, left: 4, zIndex: 1 }}
                      />
                    )}

                    <CardContent sx={{ textAlign: 'center', pb: 1, pt: !viewingTrash ? 5 : 2 }}>
                      <Box sx={{ mb: 1.5 }}>
                        {docItem.type === 'folder' ? (
                          <FolderIcon color="primary" sx={{ fontSize: 64 }} />
                        ) : isImage(docItem) && (docItem.downloadURL || docItem.fileUrl) ? (
                          <Box
                            component="img"
                            src={docItem.downloadURL || docItem.fileUrl}
                            alt={docItem.fileName || 'Image'}
                            sx={{
                              width: '100%',
                              height: 120,
                              objectFit: 'cover',
                              borderRadius: 1
                            }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <FileIcon color="action" sx={{ fontSize: 64 }} />
                        )}
                      </Box>
                      <Typography variant="body2" fontWeight={500} noWrap title={docItem.fileName}>
                        {docItem.fileName || 'Untitled'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {docItem.type === 'folder' ? 'Folder' : formatFileSize(docItem.fileSize || 0)}
                      </Typography>
                      {docItem.category && docItem.type !== 'folder' && (
                        <Chip
                          label={getCategoryLabel(docItem)}
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ mt: 0.5 }}
                        />
                      )}
                      {docItem.accessGrantedTo?.length > 0 && (
                        <Chip
                          label={`${docItem.accessGrantedTo.length} member(s)`}
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ mt: 0.5, ml: 0.5 }}
                        />
                      )}
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'center', pt: 0 }}>
                      {viewingTrash ? (
                        <>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handleRestore(docItem); }}
                            title="Restore"
                            color="primary"
                          >
                            <RestoreIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handlePermanentDeleteClick(docItem); }}
                            title="Delete permanently"
                            color="error"
                          >
                            <DeleteForeverIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, docItem)}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          );
        }

        // ---- GALLERY VIEW ----
        if (viewMode === 'gallery') {
          return (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {processedDocs.map((docItem) => (
                <Grid item xs={6} sm={4} md={3} lg={2} key={docItem.id}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      position: 'relative',
                      border: selectedIds.has(docItem.id) ? '2px solid' : 'none',
                      borderColor: selectedIds.has(docItem.id) ? 'primary.main' : 'transparent',
                      '&:hover': { boxShadow: 6 }
                    }}
                    onClick={() => {
                      if (viewingTrash) return;
                      if (docItem.type === 'folder') {
                        handleFolderClick(docItem);
                      } else {
                        handleImageClick(docItem);
                      }
                    }}
                  >
                    {!viewingTrash && (
                      <Checkbox
                        checked={selectedIds.has(docItem.id)}
                        onChange={() => handleToggleSelect(docItem.id)}
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          left: 4,
                          zIndex: 1,
                          bgcolor: 'rgba(255,255,255,0.7)',
                          borderRadius: '4px'
                        }}
                      />
                    )}

                    <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                      {docItem.type === 'folder' ? (
                        <Box sx={{ textAlign: 'center', py: 3 }}>
                          <FolderIcon color="primary" sx={{ fontSize: 64 }} />
                          <Typography variant="caption" fontWeight={500} display="block" sx={{ mt: 0.5, px: 1 }} noWrap>
                            {docItem.fileName || 'Folder'}
                          </Typography>
                        </Box>
                      ) : (
                        <>
                          <Box
                            component="img"
                            src={docItem.downloadURL || docItem.fileUrl || ''}
                            alt={docItem.fileName || 'Image'}
                            sx={{
                              width: '100%',
                              height: 160,
                              objectFit: 'cover',
                              display: 'block'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              if (e.target.nextSibling) {
                                e.target.nextSibling.style.display = 'flex';
                              }
                            }}
                          />
                          <Box
                            sx={{
                              display: 'none',
                              width: '100%',
                              height: 160,
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: 'action.hover'
                            }}
                          >
                            <ImageIcon sx={{ fontSize: 64, color: 'action.disabled' }} />
                          </Box>
                          <Box sx={{ p: 1 }}>
                            <Typography variant="caption" fontWeight={500} noWrap display="block">
                              {docItem.fileName || 'Image'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {formatFileSize(docItem.fileSize || 0)}
                            </Typography>
                            {docItem.accessGrantedTo?.length > 0 && (
                              <Chip
                                label={`${docItem.accessGrantedTo.length} shared`}
                                size="small"
                                color="success"
                                variant="outlined"
                                sx={{ mt: 0.5 }}
                              />
                            )}
                          </Box>
                        </>
                      )}
                    </CardContent>

                    <CardActions sx={{ justifyContent: 'flex-end', pt: 0, px: 1, pb: 1 }}>
                      {viewingTrash ? (
                        <>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handleRestore(docItem); }}
                            color="primary"
                          >
                            <RestoreIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handlePermanentDeleteClick(docItem); }}
                            color="error"
                          >
                            <DeleteForeverIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, docItem)}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          );
        }

        return null;
      })()}

      {/* ===== CONTEXT MENU ===== */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {menuItem?.type !== 'folder' && (
          <MenuItem onClick={() => {
            handleDownload(menuItem);
            handleMenuClose();
          }}>
            <DownloadIcon sx={{ mr: 1.5 }} fontSize="small" />
            Download
          </MenuItem>
        )}
        <MenuItem onClick={() => {
          handleRenameClick(menuItem);
          handleMenuClose();
        }}>
          <EditIcon sx={{ mr: 1.5 }} fontSize="small" />
          Rename
        </MenuItem>
        <MenuItem onClick={() => {
          handleMoveClick(menuItem);
          handleMenuClose();
        }}>
          <MoveIcon sx={{ mr: 1.5 }} fontSize="small" />
          Move
        </MenuItem>
        {menuItem?.type !== 'folder' && (
          <MenuItem onClick={() => {
            handleShareClick(menuItem);
            handleMenuClose();
          }}>
            <ShareIcon sx={{ mr: 1.5 }} fontSize="small" />
            Share
          </MenuItem>
        )}
        {menuItem?.type !== 'folder' && (
          <MenuItem
            onClick={() => {
              handleCopyToPersonal(menuItem);
              handleMenuClose();
            }}
            disabled={copying}
          >
            <CopyIcon sx={{ mr: 1.5 }} fontSize="small" />
            Copy to Personal
          </MenuItem>
        )}
        <MenuItem onClick={() => {
          handleTrashClick(menuItem);
          handleMenuClose();
        }}>
          <DeleteIcon sx={{ mr: 1.5 }} fontSize="small" color="error" />
          <Typography color="error">Delete</Typography>
        </MenuItem>
      </Menu>

      {/* ===== UPLOAD DIALOG ===== */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => !uploading && setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>{uploadDialogTitle}</DialogTitle>
        <DialogContent>
          {uploadError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {uploadError}
            </Alert>
          )}

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="caption">
              {storageType === 'photos'
                ? 'Photos uploaded to the Family Photo Vault are restricted. Admins control access per family member.'
                : 'Documents uploaded to the Family Vault are restricted. Admins control access per family member.'}
            </Typography>
          </Alert>

          {currentPath !== '/' && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Uploading to: <strong>{currentPath}</strong>
            </Typography>
          )}

          <Typography variant="caption" color="text.secondary">
            Maximum file size: 100MB
          </Typography>

          <Box sx={{ mt: 1, mb: filesToUpload.length > 0 ? 1 : 2 }}>
            <input
              type="file"
              multiple
              accept={fileAcceptType}
              onChange={(e) => setFilesToUpload(Array.from(e.target.files))}
              style={{ width: '100%' }}
            />
          </Box>

          {filesToUpload.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Selected files ({filesToUpload.length}):
              </Typography>
              {filesToUpload.map((file, index) => (
                <Typography key={index} variant="caption" display="block">
                  • {file.name} ({formatFileSize(file.size)})
                </Typography>
              ))}
            </Box>
          )}

          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={uploadCategory}
              label="Category"
              onChange={(e) => setUploadCategory(e.target.value)}
              disabled={uploading}
            >
              {categories.map((cat) => (
                <MenuItem key={cat.value} value={cat.value}>
                  {cat.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="caption" sx={{ mt: 1 }}>
                Uploading files...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setUploadDialogOpen(false); setFilesToUpload([]); setUploadError(null); }} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={handleUpload} variant="contained" disabled={uploading || filesToUpload.length === 0}>
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== NEW FOLDER DIALOG ===== */}
      <Dialog
        open={folderDialogOpen}
        onClose={() => setFolderDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Folder in Vault</DialogTitle>
        <DialogContent>
          {currentPath !== '/' && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Creating in: <strong>{currentPath}</strong>
            </Typography>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Folder Name"
            fullWidth
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleCreateFolder();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setFolderDialogOpen(false); setNewFolderName(''); }}>
            Cancel
          </Button>
          <Button onClick={handleCreateFolder} variant="contained" disabled={!newFolderName.trim()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== SOFT DELETE (TRASH) CONFIRMATION DIALOG ===== */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
      >
        <DialogTitle>Move to Trash</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to move "{documentToDelete?.fileName}" to trash?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            You can restore it later from the trash view. Shared access will be preserved.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleSoftDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Moving...' : 'Move to Trash'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== PERMANENT DELETE CONFIRMATION DIALOG ===== */}
      <Dialog
        open={permanentDeleteDialogOpen}
        onClose={() => !permanentDeleting && setPermanentDeleteDialogOpen(false)}
      >
        <DialogTitle>Permanently Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to permanently delete "{documentToPermanentDelete?.fileName}"?
          </Typography>
          <Alert severity="error" sx={{ mt: 1 }}>
            This will revoke access for all shared members. This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermanentDeleteDialogOpen(false)} disabled={permanentDeleting}>
            Cancel
          </Button>
          <Button onClick={handlePermanentDelete} color="error" variant="contained" disabled={permanentDeleting}>
            {permanentDeleting ? 'Deleting...' : 'Delete Forever'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== RENAME DIALOG ===== */}
      <Dialog
        open={renameDialogOpen}
        onClose={() => setRenameDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Rename {documentToRename?.type === 'folder' ? 'Folder' : 'File'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New Name"
            fullWidth
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleRename();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRenameDialogOpen(false); setRenameValue(''); setDocumentToRename(null); }}>
            Cancel
          </Button>
          <Button onClick={handleRename} variant="contained" disabled={!renameValue.trim()}>
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== MOVE DIALOG (FOLDER PICKER) ===== */}
      <Dialog
        open={moveDialogOpen}
        onClose={() => { setMoveDialogOpen(false); setDocumentToMove(null); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { minHeight: 400, maxHeight: 600 } }}
      >
        <DialogTitle>
          Move "{documentToMove?.fileName}" to...
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {/* Breadcrumbs for move dialog */}
          <Box sx={{ px: 3, py: 2, bgcolor: 'background.default' }}>
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
              {getMoveBreadcrumbs().map((crumb, index) => (
                <Link
                  key={crumb.path}
                  component="button"
                  variant="body2"
                  onClick={() => setMoveDestination(crumb.path)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    textDecoration: 'none',
                    color: index === getMoveBreadcrumbs().length - 1 ? 'text.primary' : 'primary.main',
                    fontWeight: index === getMoveBreadcrumbs().length - 1 ? 600 : 400,
                    '&:hover': { textDecoration: index === getMoveBreadcrumbs().length - 1 ? 'none' : 'underline' }
                  }}
                >
                  {index === 0 && <FolderIcon sx={{ mr: 0.5 }} fontSize="small" />}
                  {crumb.name}
                </Link>
              ))}
            </Breadcrumbs>
          </Box>

          {/* Folders in current move destination */}
          <List sx={{ pt: 0 }}>
            {getFoldersInPath(moveDestination).length === 0 ? (
              <ListItem>
                <ListItemText
                  primary="No subfolders"
                  secondary="Click 'Move Here' to move the item to this location."
                  primaryTypographyProps={{ color: 'text.secondary', fontStyle: 'italic' }}
                />
              </ListItem>
            ) : (
              getFoldersInPath(moveDestination)
                .filter(f => f.id !== documentToMove?.id)
                .map((folder) => (
                  <ListItem
                    key={folder.id}
                    button
                    onClick={() => setMoveDestination(folder.path)}
                  >
                    <FolderIcon color="primary" sx={{ mr: 2 }} />
                    <ListItemText
                      primary={folder.name}
                      secondary={folder.path}
                    />
                  </ListItem>
                ))
            )}
          </List>

          {/* Selected destination display */}
          <Box sx={{ px: 3, py: 2, bgcolor: 'action.hover' }}>
            <Typography variant="caption" color="text.secondary">
              Selected Destination:
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {moveDestination === '/' ? 'Root Folder' : moveDestination}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => { setMoveDialogOpen(false); setDocumentToMove(null); }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleMove}
            disabled={moveDestination === (documentToMove?.folderPath || '/')}
          >
            Move Here
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== SHARE / ACCESS CONTROL DIALOG ===== */}
      <Dialog
        open={openShareDialog}
        onClose={() => !sharing && setOpenShareDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Share Vault Document</Typography>
            <IconButton
              size="small"
              onClick={() => setOpenShareDialog(false)}
              disabled={sharing}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Share "{documentToShare?.fileName}" with family members:
          </Typography>

          {/* Permission level selection */}
          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <FormLabel component="legend">Permission Level</FormLabel>
            <RadioGroup
              row
              value={sharePermission}
              onChange={(e) => setSharePermission(e.target.value)}
            >
              <FormControlLabel value="view" control={<Radio />} label="View Only" />
              <FormControlLabel value="download" control={<Radio />} label="Can Download" />
            </RadioGroup>
          </FormControl>

          {/* Select family members */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Select family members:
          </Typography>
          <Paper variant="outlined" sx={{ maxHeight: 250, overflow: 'auto', p: 1, mb: 2 }}>
            <List dense>
              {familyMembers.length === 0 ? (
                <ListItem>
                  <ListItemText
                    primary="No family members available"
                    primaryTypographyProps={{ color: 'text.secondary', fontStyle: 'italic' }}
                  />
                </ListItem>
              ) : (
                familyMembers.map((member) => {
                  const alreadyHasAccess = documentToShare?.accessGrantedTo?.some(
                    (access) => access.userId === member.userId
                  );
                  return (
                    <ListItem
                      key={member.userId}
                      dense
                      button
                      onClick={() => !alreadyHasAccess && handleMemberToggle(member.userId)}
                      disabled={alreadyHasAccess}
                    >
                      <Checkbox
                        edge="start"
                        checked={selectedMembers.includes(member.userId) || alreadyHasAccess}
                        disabled={alreadyHasAccess}
                        size="small"
                      />
                      <ListItemText
                        primary={member.userName}
                        secondary={member.email}
                      />
                      {alreadyHasAccess && (
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRevokeAccess(documentToShare, member.userId);
                            }}
                            title="Revoke access"
                            color="error"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      )}
                    </ListItem>
                  );
                })
              )}
            </List>
          </Paper>

          {/* Currently shared members */}
          {documentToShare?.accessGrantedTo?.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Currently shared with:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {documentToShare.accessGrantedTo.map((access, index) => (
                  <Chip
                    key={index}
                    label={`${access.userName} (${access.permissions})`}
                    size="small"
                    color="success"
                    variant="outlined"
                    onDelete={() => handleRevokeAccess(documentToShare, access.userId)}
                  />
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => { setOpenShareDialog(false); setSelectedMembers([]); }}
            disabled={sharing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleGrantAccess}
            variant="contained"
            disabled={sharing || selectedMembers.length === 0}
          >
            {sharing ? 'Granting Access...' : 'Grant Access'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== DRAG & DROP OVERLAY ===== */}
      {isDragging && (
        <Fade in>
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(25, 118, 210, 0.1)',
              border: '3px dashed',
              borderColor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              pointerEvents: 'none'
            }}
          >
            <Paper
              elevation={8}
              sx={{
                p: 4,
                backgroundColor: 'background.paper',
                borderRadius: 2,
                textAlign: 'center'
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" color="primary" gutterBottom>
                Drop files here to upload to vault
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Upload to: {currentPath === '/' ? 'Root' : currentPath}
              </Typography>
            </Paper>
          </Box>
        </Fade>
      )}

      {/* ===== SNACKBAR ===== */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ===== IMAGE LIGHTBOX ===== */}
      <ImageLightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        images={lightboxImages}
        currentIndex={lightboxIndex}
        onNavigate={setLightboxIndex}
      />
    </Box>
  );
};

export default FamilyVaultTab;
